import { UserProps } from "@customer-tools-web/component-authentication";
import * as dayjs from "dayjs";
import {
  flatten,
  forEach,
  get,
  isEmpty,
  isUndefined,
  omitBy,
  padStart,
  some,
  sortBy as lodashSortBy,
  values,
} from "lodash";
import * as moment from "moment";
import * as numeral from "numeral";
import * as React from "react";
import { RouteComponentProps } from "react-router";

import featureToggles from "../../config/feature-toggles.malaysia";
import { parseReportingsQueryString } from "../component-reportings/parseReportingsQueryString";

import {
  LocationLevelType,
  MIN_PSF_PRICE_VALIDATION,
  SEARCH_TIER_DEGRADE_CODE,
  CHANNEL_CODE_AUCTION,
} from "./baseConst";
import { NumberInput } from "./components/NumberInput";
import {
  DATE_FORMAT,
  INVALID_STRING,
  LISTING_STATUS_CODE,
  SORT,
  LISTING_CROSS_LIST_TYPE_CODE,
} from "./const";
import { getUrlParams } from "./hoc/withScrollPosition";
import { parseQueryString } from "./parseQueryString";
import { locationPlacesV2_placesV2_data } from "../../__generated__/listings";

const INFINITESIMAL_FLOAT = 0.00000001;
const transferInfinitesimalFloat = (num: number) =>
  Math.abs(num) > INFINITESIMAL_FLOAT ? num : 0;
export const toFixed = (num: number, fixed: number = 0) => {
  const multiple = Math.pow(10, fixed);
  const format = fixed > 0 ? "0." + multiple.toString().slice(1) : "0";
  return numeral(transferInfinitesimalFloat(num)).format(format);
};

export const toThousand = (num: number | null, fixed: number = 2) => {
  if (num === null) {
    return null;
  }
  const multiple = Math.pow(10, fixed);
  const format = fixed > 0 ? "0,0." + multiple.toString().slice(1) : "0,0";
  return numeral(transferInfinitesimalFloat(num) ? num : 0).format(format);
};

export const toThousandWithUnit = (
  num: number | null,
  fixed: number = 2,
  unitMillion?: string,
  divisor: number = 1
) => {
  if (num === null) {
    return null;
  }
  const value = divisor
    ? numeral(transferInfinitesimalFloat(num)).divide(divisor).value()
    : transferInfinitesimalFloat(num);
  if (unitMillion) {
    const format = toThousand(value, fixed);
    return `${format} ${unitMillion}`;
  } else {
    return toThousand(value, fixed);
  }
};

export const hasErrorMessage = (obj: object, requireValue?: object) => {
  return (
    some(values(obj), (error) => !isEmpty(error)) ||
    (requireValue && some(values(requireValue), (value) => isEmpty(value)))
  );
};

export const addSplit = (
  list: Array<string | undefined | null | number>,
  split: string
) => {
  const regMid = new RegExp(` *${split}( *${split})+`, "g");
  const regStartEnd = new RegExp(`^ *${split} *| *${split} *$`, "g");
  return list.join(split).replace(regMid, split).replace(regStartEnd, "");
};

export const hasError = (errors: any): boolean => {
  if (typeof errors !== "object") {
    return true;
  }
  const errorValues = omitBy(errors, isUndefined);
  if (isEmpty(errorValues)) {
    return false;
  }

  let isInvalid = false;
  for (const key in errorValues) {
    if (hasError(errorValues[key])) {
      isInvalid = true;
      break;
    }
  }
  return isInvalid;
};

export const deepOmitBy = (
  obj: object,
  predicate: (value: any) => boolean
): object => {
  const newObj = omitBy(obj, predicate);
  for (const key in newObj) {
    if (typeof newObj[key] === "object" && !React.isValidElement(newObj[key])) {
      newObj[key] = deepOmitBy(newObj[key], predicate);
    }
  }
  return omitBy(newObj, predicate);
};

const ISOFORMAT = "YYYY-MM-DD";
export const calcDate = (from: any, to?: any) => {
  const fromTime = dayjs(dayjs(from).format(ISOFORMAT)).toDate().getTime();
  const toTime = to
    ? dayjs(dayjs(to).format(ISOFORMAT)).toDate().getTime()
    : dayjs(dayjs().format(ISOFORMAT)).toDate().getTime();

  return Math.floor((fromTime - toTime) / 3600 / 24 / 1000);
};

export const isToday = (date: any) => {
  return dayjs(dayjs().format(ISOFORMAT)).isSame(
    dayjs(dayjs(date).format(ISOFORMAT))
  );
};

export const isInvalid = (item: any) => {
  const isUndefined = item === undefined;
  const isNull = item === null;
  const isEmptyObject =
    item && typeof item === "object" && Object.keys(item).length === 0;
  const isEmptyString = item === "";
  const isInvalidNumber = typeof item === "number" && isNaN(item);
  return (
    isUndefined || isNull || isEmptyObject || isEmptyString || isInvalidNumber
  );
};

export const isValid = (item: any) => {
  return !isInvalid(item);
};

export const getIntegerRules = (
  t: (str: string, args?: object) => string,
  messagePath: string,
  required: boolean = false,
  minLength: number = 0,
  maxLength: number = 15,
  canZero: boolean = false,
  minValue?: number
) => {
  return [
    {
      required,
      type: "integer",
      validator: (
        _: any,
        value: number | string | undefined,
        callback: (message?: string) => void
      ) => {
        const val = NumberInput.transferToNumber(value);
        let message: undefined | string;

        if (isNaN(val)) {
          if (required) {
            message = t("commonMessage.required");
          }
        } else if (!canZero && val === 0) {
          message = t(`${messagePath}.warnings.format`);
        } else if (minValue && val < minValue) {
          message = t(`${messagePath}.warnings.minValue`, {
            min: minValue.toLocaleString(),
          });
        } else if (maxLength && `${val}`.length > maxLength) {
          message = t(`${messagePath}.warnings.length`);
        } else if (minLength && `${val}`.length < minLength) {
          message = t(`${messagePath}.warnings.min`);
        }
        callback(message || undefined);
      },
    },
  ];
};

export const getDate = (
  date: string,
  today: string,
  daysUnit: string,
  withTime?: boolean
) => {
  const dayObj = dayjs(date);
  if (!dayObj.isValid()) {
    return INVALID_STRING;
  }
  const offsetDate = calcDate(date);
  let result = isToday(date)
    ? today
    : `${offsetDate} ${daysUnit}${dayObj.format(DATE_FORMAT.dmy_s)}`;

  if (featureToggles.AUTO_UPGRADE && withTime) {
    const dayFormat24 = dayObj.format(DATE_FORMAT.dmy_s);
    const dayFormat12 = dayObj.format(DATE_FORMAT.time_12);

    result = isToday(date)
      ? `<strong>${today}, </strong>`
      : `<strong>${offsetDate} ${daysUnit}</strong>`;

    result += `${dayFormat24}, ${dayFormat12}`;
  }

  return result;
};

export const goBack = (
  history: RouteComponentProps["history"],
  url: string,
  state?: object
) => {
  const params = getUrlParams(url);
  history.push(url + params, { ...state, autoScroll: true });
};

export const convertQueryParamToNumber = (param?: string): number | undefined =>
  param !== undefined && typeof Number(param) === "number"
    ? Number(param)
    : undefined;

export const convertQueryParamToNumbers = (param?: string) =>
  param ? param.split(",").map((x) => Number(x)) : undefined;

export const checkAutoUpgrade = (autoUpgrade?: string) => {
  if (autoUpgrade === "1") return true;
  if (autoUpgrade === "2") return false;
  return undefined;
};

export const mapListingStatus = (
  listingStatus: string | number | undefined
): number => {
  const status = listingStatus || LISTING_STATUS_CODE.ONLINE;

  const numericStatus = Number(status);
  if (!isNaN(numericStatus)) {
    return numericStatus;
  }
  const statusMapping: { [key: string]: number } = {
    DRAFT: LISTING_STATUS_CODE.DRAFT,
    ACT: LISTING_STATUS_CODE.ONLINE,
    DEL: LISTING_STATUS_CODE.OFFLINE,
    EXP: LISTING_STATUS_CODE.EXPIRED,
  };
  return statusMapping[status] || LISTING_STATUS_CODE.ONLINE;
};

export const generateListingsQueryVariables = (
  param: string,
  user: UserProps["user"]
) => {
  const {
    channel,
    level1,
    level2,
    level3,
    level5,
    level3Text,
    level4Text,
    level5Text,
    q,
    sortBy,
    tier,
    lister,
    agency,
    branch,
    listingStatus,
    propertyType,
    quality,
    page,
    listingListerType,
    categoryType,
    unitType,
    fromPrice,
    toPrice,
    autoUpgrade,
    propertyTypeCodes,
    isRequiredLocationUpdate,
  } = parseQueryString(param);
  const { organisation } = user;
  const userOrgId = organisation ? organisation.id : undefined;
  const propertyTypeCode =
    propertyType && propertyType.indexOf("-") > -1
      ? propertyType.split("-")[1]
      : propertyType;
  const tierCode = convertQueryParamToNumber(tier);
  const propertyTypeCodesSplit =
    propertyTypeCodes &&
    propertyTypeCodes
      .split(",")
      .map(Number)
      .filter((value) => !isNaN(value) && valueNotNull(value));

  const location = featureToggles.LSOT
    ? {
        locationLevel1NanoId: level1,
        locationLevel2NanoId: level2,
        locationLevel3NanoId: level3,
        locationLevel5NanoId: level5,
      }
    : {
        locationLevel1: convertQueryParamToNumber(level1),
        locationLevel2: convertQueryParamToNumber(level2),
        locationLevel3: convertQueryParamToNumber(level3),
        locationLevel5: convertQueryParamToNumber(level5),
      };

  let channelCode = convertQueryParamToNumber(channel);
  let isAuction;
  if (channelCode === CHANNEL_CODE_AUCTION) {
    isAuction = true;
    channelCode = undefined;
  }

  return {
    statusCode: mapListingStatus(listingStatus),
    channelCode,
    isAuction,
    ...location,
    locationLevel3Text: level3Text,
    locationLevel4Text: level4Text,
    locationLevel5Text: level5Text,
    propertyTypeCode: convertQueryParamToNumber(propertyTypeCode),
    searchText: q,
    quality: quality ? quality === "true" : undefined,
    listerId: convertQueryParamToNumber(lister),
    organisationId: convertQueryParamToNumber(branch || agency),
    isExcludeChild: agency ? agency === branch : userOrgId === branch,
    sortBy: getValueOrDefault(sortBy, SORT.NEW_TO_OLD),
    productCode:
      tierCode && tierCode < SEARCH_TIER_DEGRADE_CODE ? tierCode : undefined,
    isDegraded: getValueOrDefault(tierCode === SEARCH_TIER_DEGRADE_CODE),
    listingListerType: listingListerType,
    limit: 50,
    page: getValueOrDefault(page, "1"),
    propertyCategoryTypeCode: convertQueryParamToNumber(categoryType),
    unitTypeCode: convertQueryParamToNumber(unitType),
    fromPrice: convertQueryParamToNumber(fromPrice),
    toPrice: convertQueryParamToNumber(toPrice),
    isAutoUpgrade: checkAutoUpgrade(autoUpgrade),
    propertyTypeCodes: getValueOrDefault(propertyTypeCodesSplit),
    isRequiredLocationUpdate:
      featureToggles.LSOT && isRequiredLocationUpdate
        ? isRequiredLocationUpdate === "true"
        : undefined,
  };
};

export const generateReportingsQueryVariables = (
  param: string,
  user: UserProps["user"]
) => {
  const {
    channel,
    duration,
    status,
    tiers,
    quality,
    districts,
    estates,
    agency,
    branch,
    lister,
    page,
    propertyTypeCodes,
  } = parseReportingsQueryString(param);
  const { organisation } = user;
  const userOrgId = organisation ? organisation.id : undefined;
  return {
    channelCode: convertQueryParamToNumber(channel) || 1,
    timeline: duration || "1M",
    statusCode: convertQueryParamToNumber(status),
    productCode: convertQueryParamToNumber(tiers),
    quality: quality ? quality === "true" : undefined,
    locationLevel2: convertQueryParamToNumber(districts),
    locationLevel3: convertQueryParamToNumber(estates),
    sortBy: "impressionsDesc",
    limit: 50,
    page: page || "1",
    listerId: lister,
    organisationId: branch || agency,
    isExcludeChild: agency ? agency === branch : userOrgId === branch,
    propertyTypeCodes: propertyTypeCodes,
  };
};

export const formatDate = (gmtDate: any) => {
  // e.g. 27/12/2018, 10:00:00am
  const date = dayjs(gmtDate).toDate();
  return `${date.toLocaleDateString("en-GB")}, ${date.toLocaleTimeString(
    "en-US"
  )}`;
};

export const formatAging = (aging: any) => {
  // e.g. 01:00
  const secondInHour = 3600;
  const secondInMinute = 60;
  let totalSeconds = aging;
  const hours = padStart(
    Math.floor(totalSeconds / secondInHour).toString(),
    2,
    "0"
  );
  totalSeconds %= 3600;
  const minutes = padStart(
    Math.floor(totalSeconds / secondInMinute).toString(),
    2,
    "0"
  );

  return `${hours}:${minutes}`;
};

export const formatQualityParsingDate = (gmtDate: any, dateFormat: string) => {
  return moment(gmtDate).format(dateFormat);
};

export const dataTest = (field: string | undefined) => {
  return field && field.trim().replace(/\.|\s+/g, "-");
};

export const convertUTC = (
  date: string,
  isDateFrom: boolean,
  dateFormat: string = DATE_FORMAT.ymd_d
) => {
  const appendDate = isDateFrom ? `${date} 00:00:00` : `${date} 23:59:59`;
  return moment(appendDate, dateFormat + " HH:mm:ss").format();
};

export const getLastMonthDate = (dateFormat: string = DATE_FORMAT.ymd_d) => {
  const lastMonthDate = moment().subtract(1, "months").format(dateFormat);
  return lastMonthDate;
};

export function addMask(backgroundColor?: string) {
  const div = document.createElement("div");
  div.className = "ct-loading-mask";
  if (backgroundColor) {
    div.style.backgroundColor = backgroundColor;
  }
  document.body.appendChild(div);
  return {
    remove() {
      document.body.removeChild(div);
    },
  };
}

// Return '-' if the string is empty
export const formatEmptyString = (value: string) => {
  return isEmpty(value) ? "-" : value;
};

// Convert timestamp to ISO format for date picker
export const convertISO = (
  date: string,
  isDateFrom: boolean,
  isEmptyQueryParam: boolean = true
) => {
  const d = date.substring(0, 2);
  const m = isEmptyQueryParam ? date.substring(3, 5) : date.substring(2, 4);
  const y = isEmptyQueryParam ? date.substring(6) : date.substring(4);

  return isDateFrom
    ? dayjs(`${y}-${m}-${d} 00:00:00`).toISOString()
    : dayjs(`${y}-${m}-${d} 23:59:59`).toISOString();
};

export const sortBy = <T>(
  array: T[],
  field: keyof T,
  direction: "asc" | "desc"
) => {
  if (direction === "asc") {
    return lodashSortBy(array, field);
  } else {
    const ascending = lodashSortBy(array, field);
    return ascending
      .filter((i) => i[field] !== null && i[field] !== undefined)
      .reverse()
      .concat(
        ...ascending.filter((i) => i[field] === null || i[field] === undefined)
      );
  }
};

export const isTodayWithinEndDate = (endDate: string) => {
  if (!endDate) {
    return false;
  }

  const today = moment(moment(), DATE_FORMAT.dmy_s);
  const aWeekAgo = moment(moment(endDate).subtract(8, "d"), DATE_FORMAT.dmy_s); // add 8 days cause to make day 7 valid in isBetween function
  const end = moment(moment(endDate).add(1, "d"), DATE_FORMAT.dmy_s);

  return moment(today).isBetween(aWeekAgo, end);
};

export const processNumberOutput = (
  result: any,
  defaultValue: any = "-"
): number | string | undefined => {
  return Number(result) > -1 ? Number(result) : defaultValue;
};

export const addAndSubtractFromDate = (
  date: Date,
  operation: "add" | "subtract",
  value: number,
  unit?: moment.DurationInputArg2
): Date => {
  if (moment(date).isValid()) {
    switch (operation) {
      case "add":
        return moment(date).add(value, unit).toDate();
      case "subtract":
        return moment(date).subtract(value, unit).toDate();
    }
  }
  return date;
};

export const isDateValid = (
  startDate: dayjs.ConfigType,
  endDate: dayjs.ConfigType
) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return start.isBefore(end);
};

export const isCrossListing = (
  crossListTypeCode: number | null | undefined
) => {
  return !!(
    crossListTypeCode &&
    [
      LISTING_CROSS_LIST_TYPE_CODE.ORIGINAL,
      LISTING_CROSS_LIST_TYPE_CODE.CROSS_LIST,
    ].includes(crossListTypeCode)
  );
};

export const daysRemaining = (date: string, ignoreTime: boolean = false) => {
  if (!moment(date).isValid()) {
    return 0;
  }

  const d1 = moment(date);
  const d2 = moment();
  if (!ignoreTime) {
    return d1.diff(d2, "days");
  }
  const different = moment.duration(d1.diff(d2)).asDays();
  if (different >= 0) {
    return Math.ceil(different);
  }
  return Math.floor(different);
};

export const toMinDecimalPoints = (
  value: number | string | null | undefined,
  decimalPoint: number = 2
) => {
  if (!value) {
    value = 0;
  }
  return value.toLocaleString("en", {
    useGrouping: false,
    minimumFractionDigits: decimalPoint,
  });
};

export const valueNotNull = <TValue>(
  value: TValue | null | undefined
): value is TValue => {
  return value !== null && value !== undefined;
};

export const capitalizeFirstLetter = (string: string | null) => {
  if (!string) {
    return null;
  }

  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const isValidEmailFormat = (email: string): boolean => {
  return !!String(email)
    .toLowerCase()
    .match(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/);
};

type subscriptionStatusBreakdown = {
  [key: string]: Array<{
    expiryDate: string | null;
    isCarryOver: boolean;
    total: number;
    summaryType?: {
      code: number;
    };
  }>;
};
const getUniqueExpiryDates = (data: subscriptionStatusBreakdown): string[] => {
  return Object.values(data).reduce((acc, curr) => {
    forEach(curr, (item) => {
      const expiryDate = item.expiryDate ?? "";
      if (!acc.includes(expiryDate)) {
        acc.push(expiryDate);
      }
    });
    return acc;
  }, [] as string[]);
};

const fillMissingExpiryDates = (
  data: subscriptionStatusBreakdown,
  isRepricing: boolean = false
) => {
  const keys: string[] = [];
  for (const items of Object.values(data)) {
    forEach(items, (item) => {
      const expiryDate = item.expiryDate ?? "";
      const key = isRepricing
        ? `${expiryDate}_${item.summaryType?.code}`
        : expiryDate;
      if (!keys.includes(key)) {
        keys.push(key);
      }
    });
  }

  forEach(keys, (key) => {
    const [expiryDate, summaryTypeCode] = key.split("_");
    for (const [key, items] of Object.entries(data)) {
      const obj = items.find(
        (item) =>
          expiryDate === (item.expiryDate ?? "") &&
          (!summaryTypeCode ||
            Number(summaryTypeCode) === item.summaryType?.code)
      );
      if (!obj) {
        if (!data[key]) {
          data[key] = [];
        }

        data[key].push({
          expiryDate,
          isCarryOver: false,
          total: 0,
          summaryType: summaryTypeCode
            ? { code: Number(summaryTypeCode) }
            : undefined,
        });
      }
    }
  });
};

const removeZeroTotal = (
  data: subscriptionStatusBreakdown,
  expiryDates: string[]
) => {
  const flattened = flatten(Object.values(data));
  // sum up total by expiryDates, remove data if total is 0
  forEach(expiryDates, (expiryDate: string) => {
    const total = flattened.reduce((acc, curr) => {
      if (curr.expiryDate === expiryDate) {
        acc += curr.total;
      }
      return acc;
    }, 0);

    if (total === 0) {
      forEach(data, (items, key) => {
        const obj = items.find((item) => expiryDate === item.expiryDate);
        if (obj) {
          data[key] = data[key].filter(
            (item) => item.expiryDate !== expiryDate
          );
        }
      });
    }
  });
};

const removeExpiredData = (data: subscriptionStatusBreakdown) => {
  forEach(data, (items, key) => {
    data[key] = items.filter((item) =>
      item.expiryDate ? moment(item.expiryDate).isAfter(moment()) : false
    );
  });
};

const sortDataByExpiryDate = (
  data: subscriptionStatusBreakdown,
  isAscending: boolean = true
) => {
  const direction = isAscending ? "asc" : "desc";
  forEach(data, (items, key) => {
    data[key] = sortBy(items, "expiryDate", direction);
  });
};

const sumByKey = (
  data: subscriptionStatusBreakdown,
  isRepricing: boolean = false,
  type?: string
) => {
  if (!isRepricing || !type) {
    return data;
  }

  for (const [key, items] of Object.entries(data)) {
    const result = items.reduce((acc: any, item) => {
      const expiryDate = item.expiryDate
        ? moment(item.expiryDate).format(DATE_FORMAT.dmy_s)
        : "";
      // for provisioned credits, sum total if expiry dates and summaryType codes are the same
      // for available credits, sum total if expiry dates are the same
      const k =
        type === "provisioned"
          ? `${expiryDate}_${item.summaryType?.code}`
          : expiryDate;
      const obj = acc[k];
      if (!obj) {
        acc[k] = { ...item };
      } else {
        acc[k].total += item.total;
      }
      return acc;
    }, {});

    data[key] = Object.values(result);
  }

  return data;
};

export const massageSubscriptionStatusBreakdown = (
  data: subscriptionStatusBreakdown,
  removeZero: boolean = false,
  removeExpired: boolean = false,
  isAscending: boolean = true,
  type?: string
): subscriptionStatusBreakdown => {
  // get all unique expiryDate
  const expiryDates = getUniqueExpiryDates(data);

  const isRepricing = featureToggles.REPRICING;
  // find if expiryDate/summaryType.code is not in the array, add it in
  fillMissingExpiryDates(data, isRepricing);

  // remove zero total
  if (removeZero) {
    removeZeroTotal(data, expiryDates);
  }

  // remove expired data
  if (removeExpired) {
    removeExpiredData(data);
  }

  // sort data by expiryDate
  sortDataByExpiryDate(data, isAscending);

  // sum up total by expiryDates/summaryType.code
  return sumByKey(data, isRepricing, type);
};

export const getValueOrDefault = (
  value: any,
  defaultValue: any = undefined
) => {
  return value || defaultValue;
};

export const addMonths = (date: any, months: number): Date => {
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() + months);
  return newDate;
};

export const getPackageColor: {
  [key: string]: string;
} = {
  iplatinum: "#333F48",
  imax: "#333F48",
  igold: "#CFAE4D",
  ipro: "#CFAE4D",
  ibasic: "#0081C6",
  icore: "#0081C6",
  iclassic: "#93227E",
  "duo+": "#333F48",
  "lite+": "#333F48",
  "max+": "#333F48",
  extended: "#333F48",
};

export const hasLocationId = (location: any) => {
  if (isEmpty(location)) {
    return false;
  }
  return !isEmpty(location.nanoId) || get(location, "id", -1) > 0;
};

export const locationFindHighestLevel = (
  location: any
): LocationLevelType | null => {
  if (hasLocationId(location.level5)) {
    return LocationLevelType.Level5;
  } else if (hasLocationId(location.level3)) {
    return LocationLevelType.Level3;
  } else if (hasLocationId(location.level2)) {
    return LocationLevelType.Level2;
  } else if (hasLocationId(location.level1)) {
    return LocationLevelType.Level1;
  } else {
    return null;
  }
};

export const sortByLocationText = (
  a: locationPlacesV2_placesV2_data,
  b: locationPlacesV2_placesV2_data
) => {
  const highestLevelA = locationFindHighestLevel(a);
  const highestLevelB = locationFindHighestLevel(b);
  const aText = get(a, `${highestLevelA}.text.en_GB`, "") || "";
  const bText = get(b, `${highestLevelB}.text.en_GB`, "") || "";
  return aText.localeCompare(bText);
};

export const sortBuilding = (
  a: locationPlacesV2_placesV2_data,
  b: locationPlacesV2_placesV2_data
) => {
  // For [Building/Development], it will be sorted by Property Category Type (Residential, Commercial, Industrial, Agricultural), then by the property type (Highrise, Landed etc)
  // sort by property type code: 2,8,10,9,3,1,11,7,4,5,6,17,20,18,19,12,16,15,13,14,27,28,29,26,30,21,25,22,23,24
  // if property type code is the same, sort by sortByLocationText
  const propertyTypeOrder = [
    2, 8, 10, 9, 3, 1, 11, 7, 4, 5, 6, 17, 20, 18, 19, 12, 16, 15, 13, 14, 27,
    28, 29, 26, 30, 21, 25, 22, 23, 24,
  ];
  const propertyTypeA = propertyTypeOrder.indexOf(
    get(a, "propertyType.code", -1)
  );
  const propertyTypeB = propertyTypeOrder.indexOf(
    get(b, "propertyType.code", -1)
  );
  if (propertyTypeA === propertyTypeB) {
    return sortByLocationText(a, b);
  }
  return propertyTypeA - propertyTypeB;
};

export const isCLValidationEnabled = (firstPublishedDate: string | null) => {
  const validationStartDate = moment.utc(
    process.env.REACT_APP_FEATURE_CL_VALIDATION_START
  );
  const isPublishedBeforeCLValidation = firstPublishedDate
    ? moment.utc(firstPublishedDate).isBefore(validationStartDate)
    : false;
  return !isPublishedBeforeCLValidation && featureToggles.CL_VALIDATION;
};

export const toFixedNoRounding = (value: number, decimals: number): string => {
  const factor = Math.pow(10, decimals);
  return (Math.trunc(value * factor) / factor).toFixed(decimals);
};

export const calculatePricePerArea = (price: number, area: number): number => {
  if (area === 0) {
    return 0;
  }
  return Number(toFixedNoRounding(price / area, 2));
};

export const minPsfPriceValidation = (
  area: number,
  price: number,
  channel: number
) => {
  const channelSetting = get(MIN_PSF_PRICE_VALIDATION, channel);

  if (!channelSetting || !area || !price) {
    return {
      minPsf: 0,
      isValid: true,
    };
  }

  const minPsf = channelSetting.minPsf;
  const psf = calculatePricePerArea(price, area);
  return {
    minPsf,
    isValid: psf >= minPsf,
  };
};

export const isLCv3Listing = (listing: any) => {
  if (!featureToggles.LISTING_CREATION_V3) {
    return false;
  }
  const createdVersion = get(listing, "config.createdVersion");
  return createdVersion === 3;
};

export const getListingConfigurationDisplayText = (
  value: number | string | null
): number | string | null => (value?.toString() === "-10" ? "10+" : value);

/**
 * To avoid net::ERR_UPLOAD_FILE_CHANGED error in some browsers,
 * typically Chrome, where attached file is from Google Drive,
 * we need to clone the file before uploading to ensure the file
 * content is not changed during the upload process.
 *
 * @param file - The file to be cloned
 * @returns A promise that resolves to the cloned file
 */
export const getClonedFile = async (file: File): Promise<File> => {
  try {
    const fileBuffer = await file.arrayBuffer();

    return new File([fileBuffer], file.name, { type: file.type });
  } catch (error) {
    console.error("Error cloning file:", error);

    return file;
  }
};
